// Iter50 — /engineering full workspace page.
//
// Three sections:
//   1. My Engineering Tasks — open SRs with filter pills
//   2. Engineering Results — SRs with engineering_result, latest progress
//   3. Engineering Stats — small KPI strip
// Header: "+ New engineering request" → standalone modal,
//         "Update progress" picker → inline EngineeringResultForm.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useStore } from "../store";
import { useAuth } from "../context/AuthContext";
import { hasModuleAccess } from "../lib/moduleAccess";
import { EngineeringResultForm } from "../components/EngineeringResultForm";
import { NewEngineeringRequestModal } from "../components/NewEngineeringRequestModal";
import { UniversalFilter } from "../components/filters/UniversalFilter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "in_progress", label: "In Progress" },
  { key: "completed", label: "Completed" },
];

const STATUS_BADGE = {
  requested: { bg: "rgba(24,95,165,0.16)", fg: "#9ec3ec" },
  acknowledged: { bg: "rgba(155,89,182,0.16)", fg: "#c89adb" },
  in_progress: { bg: "rgba(243,177,74,0.16)", fg: "#f3b14a" },
  completed: { bg: "rgba(78,208,132,0.16)", fg: "#9aebc2" },
};

const PRIORITY_COLOR = { high: "#E24B4A", medium: "#f3b14a", low: "#9aa1b3" };

const progressColor = (p) => (p >= 100 ? "#4ed084" : p >= 75 ? "#9ec3ec" : p >= 25 ? "#f3b14a" : "#9aa1b3");

const mono = (size, color = "var(--text, #f0f2f8)", weight = 400, ls) => ({
  fontFamily: "DM Mono, monospace", fontSize: size, color, fontWeight: weight, letterSpacing: ls,
});

export default function Engineering() {
  const { user } = useAuth();
  const addToast = useStore((s) => s.addToast);

  const canSubmit = hasModuleAccess(user, "engineering") &&
    ["responder", "escalation_manager", "tenant_admin", "platform_admin"].includes(user?.role);

  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({ all: 0, pending: 0, in_progress: 0, completed: 0 });
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const [formSr, setFormSr] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [newRequestOpen, setNewRequestOpen] = useState(false);

  // Auto-open the new-request modal when arriving from the Service Requests hub.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("new") === "1") setNewRequestOpen(true);
    } catch (_) { /* swallow */ }
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [tk, rs, st] = await Promise.all([
        axios.get(`${API}/engineering/my-tasks?status=${filter}`),
        axios.get(`${API}/engineering/my-results?limit=200`),
        axios.get(`${API}/engineering/my-stats`),
      ]);
      setTasks(tk.data.tasks || []);
      setCounts(tk.data.counts || {});
      setResults(Array.isArray(rs.data) ? rs.data : []);
      setStats(st.data);
    } catch (err) {
      addToast({ type: "error", title: "Failed to load engineering workspace", body: err.message });
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [filter]);

  const openSrsForPicker = useMemo(
    () => tasks.filter((t) => ["requested", "acknowledged", "in_progress"].includes(t.status)),
    [tasks],
  );

  // Iter51 — universal filter wiring for §1 tasks list.
  const taskChips = useMemo(() => [
    { key: "status_chip", label: "Status", type: "select", options: [
      { value: "requested", label: "Requested", predicate: (i) => i.status === "requested" },
      { value: "acknowledged", label: "Acknowledged", predicate: (i) => i.status === "acknowledged" },
      { value: "in_progress", label: "In progress", predicate: (i) => i.status === "in_progress" },
      { value: "completed", label: "Completed", predicate: (i) => i.status === "completed" },
    ]},
    { key: "priority_chip", label: "Priority", type: "select", options: [
      { value: "high", label: "High", predicate: (i) => (i.priority || "medium") === "high" },
      { value: "medium", label: "Medium", predicate: (i) => (i.priority || "medium") === "medium" },
      { value: "low", label: "Low", predicate: (i) => (i.priority || "medium") === "low" },
    ]},
    { key: "link_chip", label: "Link", type: "select", options: [
      { value: "linked", label: "Linked to incident", predicate: (i) => Boolean(i.incident_id) },
      { value: "standalone", label: "Standalone", predicate: (i) => !i.incident_id },
    ]},
    { key: "part_chip", label: "Part #", type: "text", placeholder: "Part #",
      predicate: (i, v) => (i.description || "").toLowerCase().includes(v.toLowerCase()) },
    { key: "date_chip", label: "Created", type: "dateRange", field: "created_at" },
  ], []);
  const taskSearchText = useCallback((i) => [
    i.id, i.title, i.description, i.assignee_name, i.creator_name,
    i.incident_id, i.incident_title, i.tenant_id,
  ].filter(Boolean).join(" "), []);

  const openFormFor = (sr) => {
    setPickerOpen(false);
    setFormSr(sr);
  };

  const onSaved = () => {
    setFormSr(null);
    addToast({ type: "success", title: "Engineering progress saved" });
    refresh();
  };

  return (
    <div data-testid="engineering-workspace">
      <Header
        canSubmit={canSubmit}
        onNewRequest={() => setNewRequestOpen(true)}
        onPickToUpdate={() => setPickerOpen(true)}
      />

      {newRequestOpen && (
        <NewEngineeringRequestModal
          onClose={() => setNewRequestOpen(false)}
          onCreated={async () => {
            setNewRequestOpen(false);
            try {
              const u = new URL(window.location.href);
              if (u.searchParams.get("new")) {
                u.searchParams.delete("new");
                window.history.replaceState({}, "", u.toString());
              }
            } catch (_) { /* swallow */ }
            await refresh();
          }}
        />
      )}

      {pickerOpen && (
        <SRPicker
          srs={openSrsForPicker}
          onPick={openFormFor}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {formSr && (
        <div className="mb-6" data-testid="engineering-inline-form">
          <EngineeringResultForm
            sr={formSr}
            onSubmitted={onSaved}
            onCancel={() => setFormSr(null)}
          />
        </div>
      )}

      {/* SECTION 1 — Tasks */}
      <Section title="§1 · My Engineering Tasks" testId="engineering-tasks-section">
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="engineering-tasks-filters">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className="px-3 py-1.5 rounded transition-colors"
              style={{
                background: filter === f.key ? "rgba(24,95,165,0.16)" : "transparent",
                border: `1px solid ${filter === f.key ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
                color: filter === f.key ? "#9ec3ec" : "var(--text-dim, #9aa1b3)",
                ...mono(11, undefined, undefined, "0.5px"),
                cursor: "pointer",
              }}
              data-testid={`engineering-tasks-filter-${f.key}`}
              aria-pressed={filter === f.key}
            >
              {f.label} <span style={{ opacity: 0.65 }}>({counts[f.key] ?? 0})</span>
            </button>
          ))}
        </div>

        {tasks.length === 0 ? (
          <Empty text={loading ? "Loading…" : "No engineering tasks in this view."} testId="engineering-tasks-empty" />
        ) : (
          <UniversalFilter
            storageKey="engineering_tasks"
            items={tasks}
            getSearchText={taskSearchText}
            chips={taskChips}
            placeholder="Search id, title, part #, assignee, incident…"
            testIdPrefix="engineering-tasks-uf"
            emptyMessage="No tasks match the current filter."
          >
            {({ filtered }) => (
              <div className="rounded-md overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.10)" }} data-testid="engineering-tasks-table">
                <TableHeader cols={["SR ID", "Title / incident", "Requested", "Progress", "Status", "Priority", ""]} widths={[110, "flex-1", 100, 110, 120, 90, 140]} />
                {filtered.map((sr) => (
                  <TaskRow key={sr.id} sr={sr} onUpdate={() => openFormFor(sr)} canSubmit={canSubmit} />
                ))}
              </div>
            )}
          </UniversalFilter>
        )}
      </Section>

      {/* SECTION 2 — Recent Results */}
      <Section title="§2 · Engineering Results" testId="engineering-results-section">
        {results.length === 0 ? (
          <Empty text={loading ? "Loading…" : "No engineering progress recorded yet."} testId="engineering-results-empty" />
        ) : (
          <div className="rounded-md overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.10)" }} data-testid="engineering-results-table">
            <TableHeader cols={["SR ID", "Title / incident", "Updated", "Progress", "Evidence", "Status"]} widths={[110, "flex-1", 130, 110, 110, 120]} />
            {results.map((sr) => (
              <ResultRow key={sr.id} sr={sr} onOpen={() => canSubmit && openFormFor(sr)} clickable={canSubmit} />
            ))}
          </div>
        )}
      </Section>

      {/* SECTION 3 — Stats */}
      <Section title="§3 · Engineering Stats" testId="engineering-stats-section">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Total tasks" value={stats ? String(stats.total_tasks) : "—"} color="#9ec3ec" testId="engineering-stat-total" />
          <StatCard label="In progress" value={stats ? String(stats.in_progress) : "—"} color="#f3b14a" testId="engineering-stat-in-progress" />
          <StatCard label="Completed" value={stats ? String(stats.completed) : "—"} color="#4ed084" testId="engineering-stat-completed" />
          <StatCard label="Avg progress" value={stats ? `${stats.avg_progress_pct}%` : "—"} color={stats ? progressColor(stats.avg_progress_pct) : "#9aa1b3"} testId="engineering-stat-avg-progress" />
        </div>
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

const Header = ({ canSubmit, onNewRequest, onPickToUpdate }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
    <div>
      <h1
        style={{
          fontFamily: "'Syne', system-ui, sans-serif",
          fontWeight: 800,
          fontSize: "clamp(26px, 3.4vw, 34px)",
          color: "var(--text, #f0f2f8)",
          letterSpacing: "-0.5px",
          lineHeight: 1.05,
        }}
        data-testid="view-title"
      >
        Engineering Requests
      </h1>
      <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
        Engineering provider workspace — tasks, corrective actions & progress
      </div>
    </div>
    {canSubmit && (
      <div className="flex gap-2 self-start sm:self-auto flex-wrap">
        <button
          type="button"
          onClick={onPickToUpdate}
          className="px-4 py-2 rounded-md"
          style={{
            background: "transparent",
            color: "var(--text, #f0f2f8)",
            border: "1px solid rgba(255,255,255,0.18)",
            fontFamily: "Syne, sans-serif",
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: "0.4px",
            cursor: "pointer",
          }}
          data-testid="engineering-update-progress-btn"
        >
          📈 Update progress
        </button>
        <button
          type="button"
          onClick={onNewRequest}
          className="px-4 py-2 rounded-md"
          style={{
            background: "#185FA5",
            color: "#fff",
            fontFamily: "Syne, sans-serif",
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: "0.4px",
            cursor: "pointer",
            border: "none",
          }}
          data-testid="engineering-new-request-btn"
        >
          ＋ New engineering request
        </button>
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// SR Picker
// ---------------------------------------------------------------------------

const SRPicker = ({ srs, onPick, onClose }) => (
  <div
    className="fixed inset-0 flex items-center justify-center"
    style={{ background: "rgba(0,0,0,0.65)", zIndex: 400 }}
    onClick={onClose}
    data-testid="engineering-sr-picker-overlay"
  >
    <div
      className="rounded-md p-4"
      style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.18)", width: "min(540px, 92vw)", maxHeight: "80vh", overflowY: "auto" }}
      onClick={(e) => e.stopPropagation()}
      data-testid="engineering-sr-picker"
    >
      <div className="flex items-center justify-between mb-3">
        <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 15, color: "var(--text, #f0f2f8)" }}>
          Select engineering request to update
        </span>
        <button onClick={onClose} style={{ color: "#9aa1b3", fontSize: 18, cursor: "pointer", background: "transparent", border: "none" }} data-testid="engineering-sr-picker-close">×</button>
      </div>
      {srs.length === 0 ? (
        <Empty text="No open engineering requests available." testId="engineering-sr-picker-empty" />
      ) : (
        <div className="space-y-1.5" data-testid="engineering-sr-picker-list">
          {srs.map((sr) => (
            <button
              key={sr.id}
              type="button"
              onClick={() => onPick(sr)}
              className="w-full text-left rounded-md p-2.5"
              style={{
                background: "var(--bg-base, #111420)",
                border: "1px solid rgba(255,255,255,0.10)",
                cursor: "pointer",
              }}
              data-testid={`engineering-sr-picker-row-${sr.id}`}
            >
              <div className="flex items-center justify-between">
                <span style={mono(11, "#9ec3ec")}>{sr.id}</span>
                <StatusBadge status={sr.status} />
              </div>
              <div className="mt-1" style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 13, color: "var(--text, #f0f2f8)" }}>
                {sr.title}
              </div>
              <div className="mt-0.5" style={mono(9, "#9198b0")}>
                {sr.incident_id ? `${sr.incident_id} · ${sr.incident_title || ""}` : "Standalone"}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

const TaskRow = ({ sr, onUpdate, canSubmit }) => {
  const created = sr.created_at ? new Date(sr.created_at).toLocaleDateString() : "—";
  const pri = sr.priority || "medium";
  const isOpen = ["requested", "acknowledged", "in_progress"].includes(sr.status);
  const clickable = isOpen && canSubmit;
  const er = sr.engineering_result || {};
  const progress = er.progress_pct ?? 0;
  return (
    <Row
      testId={`engineering-tasks-row-${sr.id}`}
      widths={[110, "flex-1", 100, 110, 120, 90, 140]}
      onClick={clickable ? onUpdate : undefined}
      clickable={clickable}
    >
      <Mono color="#9ec3ec">{sr.id}</Mono>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "var(--text, #f0f2f8)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {sr.title}
        </div>
        <div style={mono(9, "#9198b0", undefined, undefined)} className="truncate">
          {sr.incident_id ? `${sr.incident_id} · ${sr.incident_title || ""}` : "Standalone"}
        </div>
      </div>
      <Mono>{created}</Mono>
      <ProgressBar value={progress} />
      <StatusBadge status={sr.status} />
      <span style={{ ...mono(10, PRIORITY_COLOR[pri] || "#9aa1b3", undefined, "0.5px"), textTransform: "uppercase" }}>
        {pri}
      </span>
      {isOpen && canSubmit ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onUpdate(); }}
          className="px-2.5 py-1 rounded"
          style={{
            background: "rgba(24,95,165,0.16)",
            border: "1px solid rgba(24,95,165,0.55)",
            color: "#9ec3ec",
            ...mono(10, undefined, undefined, "0.4px"),
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
          data-testid={`engineering-tasks-update-${sr.id}`}
        >
          Update progress →
        </button>
      ) : <span />}
    </Row>
  );
};

const ResultRow = ({ sr, onOpen, clickable }) => {
  const er = sr.engineering_result || {};
  const progress = er.progress_pct ?? 0;
  const updated = er.updated_at ? new Date(er.updated_at).toLocaleString() : "—";
  const evidenceCount = (er.evidence_attachments || []).length;
  return (
    <Row
      testId={`engineering-results-row-${sr.id}`}
      widths={[110, "flex-1", 130, 110, 110, 120]}
      onClick={clickable ? onOpen : undefined}
      clickable={clickable}
    >
      <Mono color="#9ec3ec">{sr.id}</Mono>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 500, fontSize: 12, color: "var(--text, #f0f2f8)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {sr.title}
        </div>
        <div style={mono(9, "#9198b0")} className="truncate">
          {sr.incident_id ? `${sr.incident_id} · ${sr.incident_title || ""}` : "Standalone"}
        </div>
      </div>
      <Mono>{updated}</Mono>
      <ProgressBar value={progress} />
      <Mono color={evidenceCount ? "#4ed084" : "#9198b0"}>
        {evidenceCount} file{evidenceCount === 1 ? "" : "s"}{er.completion_report ? " + PDF" : ""}
      </Mono>
      <StatusBadge status={sr.status} />
    </Row>
  );
};

const ProgressBar = ({ value }) => {
  const v = Math.max(0, Math.min(100, value || 0));
  const c = progressColor(v);
  return (
    <div className="w-full" data-testid={`engineering-progress-${v}`}>
      <div className="flex items-center justify-between mb-1">
        <span style={mono(10, c, 700)}>{v}%</span>
      </div>
      <div style={{ height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ width: `${v}%`, height: "100%", background: c, transition: "width 200ms ease" }} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const Section = ({ title, children, testId, actions }) => (
  <div className="mb-8" data-testid={testId}>
    <div className="flex items-end justify-between mb-3">
      <h2 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "var(--text, #f0f2f8)", letterSpacing: "-0.2px" }}>{title}</h2>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
    {children}
  </div>
);

const StatCard = ({ label, value, color, testId }) => (
  <div
    className="rounded-md p-3"
    style={{ background: "var(--bg-surface, #111420)", border: "1px solid rgba(255,255,255,0.12)" }}
    data-testid={testId}
  >
    <div style={mono(9, "#9198b0", undefined, "1.2px")} className="uppercase">{label}</div>
    <div className="mt-2" style={{ ...mono(22, color, 500), lineHeight: 1 }}>{value}</div>
  </div>
);

const TableHeader = ({ cols, widths }) => (
  <div className="flex items-center gap-3 px-3 py-2" style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
    {cols.map((c, i) => (
      <div
        key={i}
        style={{
          flex: widths[i] === "flex-1" ? 1 : `0 0 ${widths[i]}px`,
          width: widths[i] === "flex-1" ? undefined : widths[i],
          ...mono(9, "#9198b0", undefined, "1.2px"),
          textTransform: "uppercase",
        }}
      >
        {c}
      </div>
    ))}
  </div>
);

const Row = ({ children, widths, testId, onClick, clickable }) => (
  <div
    className="flex items-center gap-3 px-3 py-2.5"
    style={{
      background: "var(--bg-surface, #0d0f18)",
      borderBottom: "1px solid rgba(255,255,255,0.05)",
      cursor: clickable ? "pointer" : "default",
    }}
    onClick={onClick}
    data-testid={testId}
  >
    {React.Children.toArray(children).map((c, i) => (
      <div
        key={i}
        style={{
          flex: widths[i] === "flex-1" ? 1 : `0 0 ${widths[i]}px`,
          width: widths[i] === "flex-1" ? undefined : widths[i],
          minWidth: 0,
        }}
      >
        {c}
      </div>
    ))}
  </div>
);

const Mono = ({ children, color, bold }) => (
  <span style={mono(11, color || "var(--text, #f0f2f8)", bold ? 700 : 400)}>{children}</span>
);

const StatusBadge = ({ status }) => {
  const s = STATUS_BADGE[status] || { bg: "rgba(120,125,140,0.16)", fg: "#9aa1b3" };
  return (
    <span style={{
      background: s.bg, color: s.fg,
      ...mono(9, undefined, undefined, "0.5px"),
      padding: "2px 7px", borderRadius: 3, textTransform: "uppercase",
    }}>{(status || "").replace("_", " ")}</span>
  );
};

const Empty = ({ text, testId }) => (
  <div
    className="rounded-md p-6 text-center"
    style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.08)", ...mono(11, "var(--text-dim, #9198b0)") }}
    data-testid={testId}
  >
    {text}
  </div>
);
